import { getDefault } from '@pearsontechnology/aero-plugin-api';
import generateNonce from 'utils/generateNonce';
import Multimap from 'utils/Multimap';
import ToastManager from 'ToastManager';
import CoursewareItemProvider from 'services/coursewareItem/CoursewareItemProvider';
import CoursewareTargetProvider from '../coursewareTarget/CoursewareTargetProvider';
import { loadPluginView } from './loadPluginView';
import invokePlugin from './invokePlugin';
import { publishEvent } from '../EventService/EventServiceProvider';
import { INTERACTIVE } from '../coursewareItem/coursewareTypes';
import { INTERACTIVELOADED, PLUGINREMOVED } from './lifecycleEvents';
import { pendoModule } from '../../cookies/Pendo/pendo';

// coursewareId -> info
export const activePlugins = new Map();

// coursewareId -> [Array of ancestry Id's]
export const coursewareAncestry = new Map();

// parentId -> childId
export const parentToChild = new Multimap();

const replaceSpaceWithHyphen = (val) => val ? val.replace(/\s/g, '-').toLowerCase() : '';

// INIT PLUGIN

export async function startPluginShared(instanceId, entryPoint, element) {
  try {
    const pluginView = loadPluginView(entryPoint, element);
    // TODO: fix to get pluginName and title from static walkable data
    const { state, plugin } = await CoursewareItemProvider.getCourseWareItem(instanceId);
    const { type: coursewareType } = await CoursewareTargetProvider.getWalkableType(instanceId);
    const pluginName = replaceSpaceWithHyphen(plugin?.manifest?.name);
    pluginView.addTitle(state.title);
    pluginView.addClass(pluginName);
    const { ApiDriver } = getDefault();
    const pearsonUid = window.piSession.userId();
    const driver = ApiDriver.listenForConnectionsInLearnspace(instanceId, pluginView.getIframe(), 'LEARNER', coursewareType, {
      debugRequests: ['STATE_RESOURCE.SET', 'LEARNER_RESOURCE.EVALUATE'],
      script: pendoModule(pearsonUid, window.appConfig.getPendoApiKey()),
    });
    pluginView.load();
    return await driver;
  } catch (error) {
    ToastManager.notify({ message: 'There was an error initialising a plugin' });
    console.error(error); // eslint-disable-line no-console
  }

  return null;
}

export async function startPluginInitChild(instanceId, entryPoint, respondToInitChildCallback) {
  const activePlugin = activePlugins.get(instanceId);
  // TODO: fix to get pluginName and title from static walkable data
  const { state, plugin } = await CoursewareItemProvider.getCourseWareItem(instanceId);
  const { type: coursewareType } = await CoursewareTargetProvider.getWalkableType(instanceId);

  if (coursewareType === INTERACTIVE) {
    publishEvent(INTERACTIVELOADED, {
      detail: {
        instanceId,
      },
    });
  }
  const nonce = generateNonce();
  const { ApiDriver } = getDefault();
  const pearsonUid = window.piSession.userId();
  const driver = ApiDriver.listenForNonceInLearnspace(instanceId, nonce, 'LEARNER', coursewareType, {
    debugRequests: ['STATE_RESOURCE.SET', 'LEARNER_RESOURCE.EVALUATE'],
    script: pendoModule(pearsonUid, window.appConfig.getPendoApiKey()),
  });
  activePlugin.driver = driver;

  respondToInitChildCallback({ nonce, entryPoint, title: state.title, name: plugin?.manifest?.name });
}

// INIT CHILDREN PLUGIN

export function initChildWithCallback(id, childId, entryPoint, respondToInitChildCallback) {
  parentToChild.add(id, childId);
  invokePlugin(childId, entryPoint, null, respondToInitChildCallback);

  const parentAncestry = coursewareAncestry.get(id) || [];
  coursewareAncestry.set(childId, [...parentAncestry, id]);
}

// REMOVE PLUGIN / CHILDREN

function _getChildIds(instanceId) {
  let children;
  if (parentToChild.has(instanceId)) {
    children = parentToChild.get(instanceId);
  } else {
    children = [];
  }
  return children;
}

function _removePlugin(instanceId) {
  if (!activePlugins.has(instanceId)) {
    return;
  }
  publishEvent(PLUGINREMOVED, {
    detail: {
      instanceId,
    },
  });
  const entry = activePlugins.get(instanceId);
  const { driver } = entry;
  if (driver) {
    driver.cancelListenersAndSubscriptions();
  }
  activePlugins.delete(instanceId);
}

function removeDescendants(id) {
  const childIds = [..._getChildIds(id)];
  childIds.forEach((childId) => removeChild(id, childId));
}

export function removePluginInstance(instanceId) {
  removeDescendants(instanceId);
  _removePlugin(instanceId);
  coursewareAncestry.delete(instanceId);
}

export function removeChild(instanceId, childId) {
  const children = _getChildIds(instanceId);

  if (children && children.indexOf(childId) !== -1) {
    removeDescendants(childId);
    _removePlugin(childId);
    coursewareAncestry.delete(childId);
    children.splice(children.indexOf(childId), 1);
    if (children.length === 0) {
      parentToChild.delete(instanceId);
    }
  }
}
